import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Session from "supertokens-auth-react/recipe/session";
import { useHookstate } from "@hookstate/core";
import { globalUserState } from "../globalState";
import { api } from "../apiClient";

const ProfileDropdown = ({ navTone = "light" }) => {
  const userState = useHookstate(globalUserState);
  const user = userState.get(); // Get the current user object from global state
  const [fullName, setFullName] = useState(user.fullName || "User");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside (helps on mobile/touch)
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Fetch fullName from session if not in global state
  useEffect(() => {
    const fetchAvatarAndSync = async () => {
      try {
        const profileResp = await api.get("/users/me/profile");
        const profile = profileResp?.data;
        if (profile?.avatar_url) setAvatarUrl(profile.avatar_url);
        if (profile?.full_name) {
          setFullName(profile.full_name);
          if (!user.fullName) {
            userState.set({ fullName: profile.full_name });
          }
        }
      } catch (error) {
        // Don't block dropdown rendering if avatar fetch fails.
        // (e.g., auth cookie not ready yet)
      }
    };

    async function fetchFullName() {
      try {
        const sessionExists = await Session.doesSessionExist();
        if (sessionExists) {
          const accessTokenPayload = await Session.getAccessTokenPayloadSecurely();
          const nameFromSession = accessTokenPayload.FullName;
          if (nameFromSession) {
            setFullName(nameFromSession);
            // Also update global state for consistency
            if (!user.fullName) {
              userState.set({ fullName: nameFromSession });
            }
          }
          await fetchAvatarAndSync();
        }
      } catch (error) {
        console.error("Error fetching fullName from session:", error);
      }
    }
    
    // If global state doesn't have fullName, fetch from session
    if (!user.fullName) {
      fetchFullName();
    } else {
      setFullName(user.fullName);
      fetchAvatarAndSync();
    }
  }, [user.fullName, userState]);

  // After profile picture upload on /profile, refresh avatar in the app bar without full reload.
  useEffect(() => {
    const onProfileUpdated = async () => {
      try {
        const profileResp = await api.get("/users/me/profile");
        const profile = profileResp?.data;
        if (profile?.avatar_url) setAvatarUrl(profile.avatar_url);
        if (profile?.full_name) setFullName(profile.full_name);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("user-profile-updated", onProfileUpdated);
    return () => window.removeEventListener("user-profile-updated", onProfileUpdated);
  }, []);

  const handleLogout = async () => {
    if (process.env.NODE_ENV === 'development') console.log("Frontend: Attempting to sign out...");
    try {
      await Session.signOut();
      if (process.env.NODE_ENV === 'development') console.log("Frontend: Session.signOut() successful.");
      userState.set({}); // Clear user state on logout
      navigate("/auth"); // Adjust route if your login is at another path
    } catch (error) {
      console.error("Frontend: Error during Session.signOut():", error);
      alert("Logout failed. Please try again.");
    }
  };

  const accentClass = navTone === "dark" ? "text-white" : "text-primary";
  const borderClass = navTone === "dark" ? "border-white/30" : "border-primary";

  return (
    <div ref={dropdownRef} className="relative flex items-center space-x-4">
      {/* Profile Dropdown Button */}
      <button
        className="flex items-center space-x-2 focus:outline-none"
        onClick={() => setDropdownOpen((open) => !open)}
      >
        {/* Avatar (replace src with real profile pic if available) */}
        <img
          src={
            avatarUrl ||
            "https://ui-avatars.com/api/?name=" +
              encodeURIComponent(fullName || "User")
          }
          alt="profile"
          className={`w-8 h-8 rounded-full border-2 ${borderClass}`}
        />
        <span className={`font-medium truncate max-w-[100px] sm:max-w-[150px] md:max-w-none ${accentClass}`}>
          {fullName}
        </span>
        <svg className={`w-4 h-4 ${accentClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M19 9l-7 7-7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown Menu */}
      {dropdownOpen && (
        <div
          className="absolute right-0 mt-12 w-44 bg-white shadow-lg rounded-xl py-2 z-50 animate-fade-in"
          onMouseLeave={() => setDropdownOpen(false)}
        >
          <Link
            to="/profile"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setDropdownOpen(false)}
          >
            Profile
          </Link>
          <Link
            to="/settings"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setDropdownOpen(false)}
          >
            Settings
          </Link>
          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
